import React, { useState } from 'react';
import { 
  FaTag, 
  FaSearch,
  FaChartBar,
  FaTrash,
  FaCopyright,
  FaEllipsisH,
  FaExclamationTriangle,
  FaArrowLeft,
  FaChevronDown,
  FaCodeBranch,
  FaUndo
} from 'react-icons/fa';
import { message } from 'antd';

// 版本时间线组件，以时间线形式展示版本历史，不显示时间
function VersionTimeline({ prompt, onSwitchVersion, onAddVersionTag, onRemoveVersionTag, onCreateBranch, onRollbackVersion, onDeleteVersion, onBack, onCompareVersions }) {
  const [filter, setFilter] = useState('all'); // 'all', 'tagged', 'date', 'version'
  const [searchTerm, setSearchTerm] = useState('');
  const [showStats, setShowStats] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);

  // 计算版本统计信息
  const calculateStats = () => {
    if (!prompt || !prompt.versions) return null;

    const totalVersions = prompt.versions.length;
    const firstVersionDate = new Date(prompt.versions[0].createdAt);
    const lastVersionDate = new Date(prompt.versions[totalVersions - 1].createdAt);
    const daysDiff = Math.floor((lastVersionDate - firstVersionDate) / (1000 * 60 * 60 * 24));
    
    // 计算每月版本数量
    const monthlyVersions = {};
    prompt.versions.forEach(version => {
      const date = new Date(version.createdAt);
      const monthYear = `${date.getFullYear()}-${date.getMonth() + 1}`;
      monthlyVersions[monthYear] = (monthlyVersions[monthYear] || 0) + 1;
    });
    
    // 计算平均内容长度
    const totalLength = prompt.versions.reduce((sum, version) => sum + version.content.length, 0);
    const averageLength = Math.round(totalLength / totalVersions);
    
    // 计算标签数量
    const taggedVersions = prompt.versions.filter(version => version.tags && version.tags.length > 0).length;
    
    return {
      totalVersions,
      daysSinceFirst: daysDiff,
      versionsPerMonth: Object.entries(monthlyVersions).map(([month, count]) => ({ month, count })),
      averageLength,
      taggedVersions,
      changeFrequency: daysDiff > 0 ? (totalVersions / daysDiff).toFixed(2) : totalVersions
    };
  };

  // 过滤版本
  const filterVersions = () => {
    if (!prompt || !prompt.versions) return [];
    
    let filtered = [...prompt.versions];
    
    // 应用搜索
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(version => 
        (version.description && version.description.toLowerCase().includes(term)) ||
        (version.versionNumber && version.versionNumber.toLowerCase().includes(term)) ||
        (version.tags && version.tags.some(tag => tag.toLowerCase().includes(term)))
      );
    }
    
    // 应用过滤器
    switch (filter) {
      case 'tagged':
        filtered = filtered.filter(version => version.tags && version.tags.length > 0);
        break;
      case 'date':
        // 按日期排序（最新的在前）
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'version':
        // 按版本号排序
        filtered.sort((a, b) => {
          if (!a.versionNumber) return 1;
          if (!b.versionNumber) return -1;
          return a.versionNumber.localeCompare(b.versionNumber, undefined, { numeric: true });
        });
        break;
      default:
        // 默认按索引排序（最早的在前）
        filtered.sort((a, b) => prompt.versions.indexOf(a) - prompt.versions.indexOf(b));
    }
    
    return filtered;
  };

  // 添加版本标签
  const handleAddTag = () => {
    if (!tagInput.trim() || !selectedVersion) return;
    
    const versionIndex = prompt.versions.findIndex(v => v.id === selectedVersion.id);
    if (versionIndex === -1) return;
    
    const newTag = tagInput.trim();
    onAddVersionTag(prompt.id, versionIndex, newTag);
    
    setTagInput('');
    setShowTagInput(false);
    message.success('标签添加成功');
  };

  // 删除版本标签
  const handleDeleteTag = (versionIndex, tag) => {
    onRemoveVersionTag(prompt.id, versionIndex, tag);
    message.success('标签已移除');
  };

  // 处理删除版本
  const handleDeleteVersion = (versionIndex) => {
    // 如果只有一个版本，不允许删除
    if (prompt.versions.length <= 1) {
      message.error('无法删除唯一的版本');
      return;
    }
    
    setVersionToDelete(versionIndex);
    setShowDeleteModal(true);
  };

  // 确认删除版本
  const confirmDeleteVersion = () => {
    if (versionToDelete !== null) {
      onDeleteVersion(prompt.id, versionToDelete);
      setShowDeleteModal(false);
      setVersionToDelete(null);
      message.success('版本已删除');
    }
  };

  // 获取版本变化指示器
  const getChangeIndicator = (version, index) => {
    if (index === 0) return null; // 第一个版本没有变化指示器
    
    const prevVersion = prompt.versions[index - 1];
    const currentContent = version.content;
    const prevContent = prevVersion.content;
    
    // 计算内容变化百分比
    const maxLength = Math.max(currentContent.length, prevContent.length);
    let diffCount = 0;
    
    // 简单字符级比较
    for (let i = 0; i < maxLength; i++) {
      if (currentContent[i] !== prevContent[i]) {
        diffCount++;
      }
    }
    
    const changePercentage = (diffCount / maxLength) * 100;
    
    // 根据变化百分比返回不同的指示器
    if (changePercentage < 10) {
      return { type: 'minor', label: '小改动', color: 'bg-blue-500' };
    } else if (changePercentage < 30) {
      return { type: 'medium', label: '中等改动', color: 'bg-yellow-500' };
    } else {
      return { type: 'major', label: '大改动', color: 'bg-red-500' };
    }
  };

  // 按日期分组版本，但不显示日期
  const groupVersions = () => {
    const filtered = filterVersions();
    return [{ versions: filtered }];
  };

  // 处理创建分支
  const handleCreateBranch = (versionIndex) => {
    if (!selectedVersion) return;
    
    const branchData = {
      description: `从版本 ${versionIndex + 1} 创建的分支`,
      versionNumber: `${prompt.versions.length + 1}.0-branch`,
      tags: ['分支版本']
    };
    
    onCreateBranch(prompt.id, versionIndex, branchData);
    message.success('分支创建成功');
  };

  const stats = calculateStats();
  const groupedVersions = groupVersions();

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* 顶部工具栏 */}
      <div className="flex justify-between items-center p-3 border-b border-gray-100">
        <div className="flex items-center">
          <button 
            onClick={onBack}
            className="text-gray-600 hover:text-gray-900 flex items-center mr-3"
          >
            <FaArrowLeft className="mr-1" size={12} />
            <span className="text-sm">返回</span>
          </button>
          <h3 className="text-sm font-medium text-gray-800">版本历史</h3>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="relative">
            <input
              type="text"
              className="w-36 text-xs py-1 px-2 pl-6 border border-gray-200 rounded-md"
              placeholder="搜索版本..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={10} />
          </div>
          
          <div className="relative">
            <select
              className="text-xs py-1 pl-2 pr-6 border border-gray-200 rounded-md appearance-none bg-white"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">全部</option>
              <option value="tagged">已标记</option>
              <option value="date">按日期</option>
              <option value="version">按版本号</option>
            </select>
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <FaChevronDown size={8} className="text-gray-400" />
            </div>
          </div>
          
          <button
            className="text-xs py-1 px-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            onClick={onCompareVersions}
            title="对比版本"
          >
            对比版本
          </button>
          
          <button
            className={`p-1 rounded-md ${showStats ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}
            onClick={() => setShowStats(!showStats)}
            title={showStats ? "隐藏统计" : "显示统计"}
          >
            <FaChartBar size={10} />
          </button>
        </div>
      </div>
      
      {/* 版本统计 */}
      {showStats && stats && (
        <div className="p-3 bg-gray-50 border-b border-gray-100">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white p-2 rounded-md shadow-sm">
              <div className="text-xs text-gray-500 mb-1">总版本</div>
              <div className="text-base font-medium text-gray-800">{stats.totalVersions}</div>
            </div>
            
            <div className="bg-white p-2 rounded-md shadow-sm">
              <div className="text-xs text-gray-500 mb-1">已标记</div>
              <div className="text-base font-medium text-gray-800">{stats.taggedVersions}</div>
            </div>
            
            <div className="bg-white p-2 rounded-md shadow-sm">
              <div className="text-xs text-gray-500 mb-1">平均长度</div>
              <div className="text-base font-medium text-gray-800">{stats.averageLength}</div>
            </div>
          </div>
        </div>
      )}
      
      {/* 时间线视图 */}
      <div className="p-3 max-h-[500px] overflow-y-auto">
        {groupedVersions.map((group, groupIndex) => (
          <div key={groupIndex}>
            <div className="relative">
              {group.versions.map((version, index) => {
                const versionIndex = prompt.versions.findIndex(v => v.id === version.id);
                const changeIndicator = getChangeIndicator(version, prompt.versions.indexOf(version));
                const isCurrentVersion = versionIndex === prompt.currentVersionIndex;
                
                return (
                  <div 
                    key={version.id} 
                    className="mb-3"
                  >
                    {/* 版本卡片 */}
                    <div 
                      className={`p-3 border rounded-md ${
                        isCurrentVersion 
                          ? 'border-blue-300 bg-blue-50' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      } cursor-pointer transition-colors`}
                      onClick={() => onSwitchVersion(versionIndex)}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div 
                            className={`w-2 h-2 rounded-full mr-2 ${
                              isCurrentVersion 
                                ? 'bg-blue-500' 
                                : changeIndicator 
                                  ? changeIndicator.color.replace('bg-[var(--apple-', 'bg-').replace(')]', '')
                                  : 'bg-gray-300'
                            }`}
                          ></div>
                          <h4 className={`text-xs font-medium ${
                            isCurrentVersion 
                              ? 'text-blue-600' 
                              : 'text-gray-700'
                          }`}>
                            版本 {versionIndex + 1}
                            {version.versionNumber && (
                              <span className="ml-1 text-xs text-gray-500">
                                v{version.versionNumber}
                              </span>
                            )}
                            {isCurrentVersion && (
                              <span className="ml-1 text-xs text-blue-500">
                                (当前)
                              </span>
                            )}
                          </h4>
                          
                          {changeIndicator && (
                            <span className={`ml-2 text-xs px-1 py-0.5 rounded text-white ${
                              changeIndicator.color.replace('bg-[var(--apple-', 'bg-').replace(')]', '')
                            }`}>
                              {changeIndicator.label}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex space-x-1">
                          <button
                            className="text-gray-400 hover:text-blue-500 p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVersion(version);
                              setShowTagInput(true);
                            }}
                            title="添加标签"
                          >
                            <FaTag size={10} />
                          </button>
                          
                          <div className="relative group">
                            <button
                              className="text-gray-400 hover:text-gray-600 p-1"
                              title="更多操作"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FaEllipsisH size={10} />
                            </button>
                            
                            <div className="hidden group-hover:block absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-md z-10 w-32" onClick={(e) => e.stopPropagation()}>
                              <div 
                                className="text-xs p-2 hover:bg-gray-50 cursor-pointer"
                                onClick={() => onRollbackVersion(prompt.id, versionIndex)}
                              >
                                <FaUndo className="inline-block mr-1" size={10} />
                                回滚到此版本
                              </div>
                              <div 
                                className="text-xs p-2 hover:bg-gray-50 cursor-pointer"
                                onClick={() => {
                                  setSelectedVersion(version);
                                  handleCreateBranch(versionIndex);
                                }}
                              >
                                <FaCodeBranch className="inline-block mr-1" size={10} />
                                创建分支
                              </div>
                              <div 
                                className="text-xs p-2 hover:bg-gray-50 cursor-pointer text-red-500"
                                onClick={() => handleDeleteVersion(versionIndex)}
                              >
                                <FaTrash className="inline-block mr-1" size={10} />
                                删除此版本
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* 版本标签 */}
                      {version.tags && version.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {version.tags.map(tag => (
                            <span 
                              key={tag} 
                              className="inline-flex items-center text-xs px-1.5 py-0.5 bg-blue-50 text-blue-500 rounded border border-blue-100"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {tag}
                              <button
                                className="ml-1 text-gray-400 hover:text-red-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTag(versionIndex, tag);
                                }}
                                title="移除标签"
                              >
                                <FaTrash size={8} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {version.description && (
                        <p className="text-xs text-gray-600 mb-2">
                          {version.description}
                        </p>
                      )}
                      
                      {/* 版本内容预览 */}
                      <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-100 max-h-20 overflow-y-auto">
                        {version.content.length > 100 
                          ? version.content.substring(0, 100) + '...' 
                          : version.content}
                      </div>
                      
                      {/* 版本时间 */}
                      <div className="mt-1 text-xs text-gray-400 flex justify-end">
                        {new Date(version.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      
      {/* 版权信息 */}
      <div className="p-2 border-t border-gray-100 flex justify-center items-center text-xs text-gray-400">
        <FaCopyright className="mr-1" size={8} /> 2025 摸鱼小李 版权所有
      </div>
      
      {/* 标签输入弹窗 */}
      {showTagInput && selectedVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
          <div className="bg-white p-3 w-72 rounded-lg shadow-lg">
            <h3 className="text-sm font-medium text-gray-800 mb-2 flex items-center">
              <FaTag className="mr-1 text-blue-500" size={10} />
              添加标签
            </h3>
            
            <div className="mb-3">
              <input
                type="text"
                className="w-full text-xs p-1.5 border border-gray-200 rounded"
                placeholder="输入标签名称"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                autoFocus
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                className="text-xs py-1 px-2 border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50"
                onClick={() => {
                  setShowTagInput(false);
                  setTagInput('');
                }}
              >
                取消
              </button>
              <button
                className="text-xs py-1 px-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                onClick={handleAddTag}
                disabled={!tagInput.trim()}
              >
                添加
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 删除版本确认弹窗 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
          <div className="bg-white p-3 w-72 rounded-lg shadow-lg">
            <div className="flex items-center text-red-500 mb-2">
              <FaExclamationTriangle className="mr-1" size={12} />
              <h3 className="text-sm font-medium">确认删除版本</h3>
            </div>
            
            <p className="text-xs text-gray-600 mb-3">
              您确定要删除版本 {versionToDelete !== null ? versionToDelete + 1 : ''}？此操作无法撤销。
              {versionToDelete === prompt.currentVersionIndex && (
                <span className="block mt-1 text-xs text-red-500">
                  注意：这是当前使用的版本，删除后将切换到最新版本。
                </span>
              )}
            </p>
            
            <div className="flex justify-end space-x-2">
              <button
                className="text-xs py-1 px-2 border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50"
                onClick={() => {
                  setShowDeleteModal(false);
                  setVersionToDelete(null);
                }}
              >
                取消
              </button>
              <button
                className="text-xs py-1 px-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={confirmDeleteVersion}
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VersionTimeline;
